import { Component, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Client, CommandStatus, PageInfo, PermissionsEnum, ScheduleLogsCommandsDataOut, ScheduleLogsCommandsQuery, SortingType } from '../../../../../api/api-reference';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AuthService } from '../../../../../services/auth.service';
import { CommonModule } from '@angular/common';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { SortComponent } from '../../../../core/components/sort/sort.component';
import { DatepickerComponent } from '../../../../core/components/datepicker/datepicker.component';
import { SearchComponent } from '../../../../core/components/search/search.component';
import { PaginationComponent } from '../../../../core/components/pagination/pagination.component';
import { DateRangeInput } from '../../../../core/models/date-range-input';
import { TranslateModule } from '@ngx-translate/core';
import { StatusComponent } from '../../../../core/components/status/status.component';
import { FileService } from '../../../../../services/file.service';
import { ClipboardService } from '../../../../../services/clipboard.service';
import { processDateRange } from '../../../../../helper/date-utils';
import { generateFileNameAppendix } from '../../../../../helper/download-filename';

@Component({
  selector: 'app-scheduler-commands',
  standalone: true,
  imports: [CommonModule,
    RouterLink,
    AngularSvgIconModule,
    SortComponent,
    DatepickerComponent,
    SearchComponent,
    PaginationComponent,
    TranslateModule,
    StatusComponent
  ],
  templateUrl: './scheduler-commands.component.html',
  styleUrl: './scheduler-commands.component.scss'
})
export class SchedulerCommandsComponent implements OnInit {
  data: ScheduleLogsCommandsDataOut[] = [];

  public pageSize: number = 10;
  public pageNumber: number = 1;
  public currentPage: number = 1;
  public numberOfCommands: number = 0;
  public lastPageSize: number = 1;
  public selectedRead: ScheduleLogsCommandsDataOut | null = null;
  commandStatuses = Object.values(CommandStatus).map(s => s.toUpperCase());

  isDatepickerOpen = false;
  PermissionsEnum = PermissionsEnum;
  schedulerId: string = "";

  requestData = new ScheduleLogsCommandsQuery({
    pageInfo: new PageInfo({ pageSize: this.pageSize, page: this.pageNumber }),
    search: '',
    startDate: undefined,
    endDate: undefined,
    sorting: SortingType.Descending,
    commandStatus: undefined,
    schedulerId: this.schedulerId
  })

  constructor(
    private toastr: ToastrService,
    private client: Client,
    private route: ActivatedRoute,
    public authService: AuthService,
    private fileService: FileService,
    private router: Router,
    public clipboardService: ClipboardService
  ) {
  }

  ngOnInit(): void {
    this.requestData.schedulerId = history.state?.schedulerLogId;
    this.schedulerId = history.state?.schedulerId;
    if (this.requestData.schedulerId && this.schedulerId)
      this.reloadTable(1);
    else
      this.router.navigate(['/settings/scheduler']);
  }

  reloadTable(page?: number) {
    this.pageNumber = page || 0;
    if (page) this.requestData!.pageInfo!.page = this.pageNumber;

    this.client.queryScheduleLogsCommands(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data!;
        this.numberOfCommands = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfCommands / this.pageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      }
    })
  }

  goBack() {
    this.router.navigate(['/settings/scheduler/monitoring'], {
      state: { monitoringId: this.schedulerId }
    });
  }

  download() {
    const fileNameApendix = generateFileNameAppendix();

    this.client.downloadScheduleLogCommands(this.requestData).subscribe({
      next: (response) => {
        this.fileService.downloadFile(
          response.data,
          response.fileName || `scheduler-logs_commands_${fileNameApendix}`,
        );
      },
      error: (err) => {
        this.toastr.error(err);
      }
    })
  }

  onStatusOption(selectedStatus?: string) {
    if (selectedStatus !== undefined) {
      this.requestData.commandStatus =
        selectedStatus === 'ALL'
          ? undefined
          : (selectedStatus as CommandStatus);
    }
    this.reloadTable();
  }

  selectedDate: string | null = '';
  onDateRangeChanged(input: DateRangeInput): void {
    const result = processDateRange(input);

    this.requestData.startDate = result.startDate;
    this.requestData.endDate = result.endDate;
    this.selectedDate = result.selectedDate;
    this.filterDatesInTable();
  }

  toggleDatepicker() {
    this.isDatepickerOpen = !this.isDatepickerOpen;
  }

  handleRowClick(row: ScheduleLogsCommandsDataOut) {
    if (row.status == CommandStatus.Failed) this.selectedRead = row;
  }

  filterDatesInTable() {
    this.client.queryScheduleLogsCommands(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data!;
        this.numberOfCommands = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfCommands / this.pageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  clearDates(): void {
    this.requestData.startDate = undefined;
    this.requestData.endDate = undefined;
    this.reloadTable();
  }

  toggleModal() {
    this.selectedRead = this.selectedRead = null;
  }

  //#region pagination, sort, search 
  pageChange(value: number) {
    this.currentPage = value;
    this.reloadTable(this.currentPage);
  }

  onSortOption(selectedSort?: SortingType) {
    if (selectedSort) this.requestData.sorting = selectedSort;
    this.reloadTable();
  }

  searchChange(searchText: string) {
    this.requestData.search = searchText;
    this.reloadTable(1);
  }
  //#endregion
}
